import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';

import type { LayoutAction, LayoutActions, LayoutState } from './types';
import { BREAKPOINTS, SIDEBAR_COLLAPSED_KEY } from './types';

export const LayoutStateContext = createContext<LayoutState | null>(null);
export const LayoutActionsContext = createContext<LayoutActions | null>(null);

/**
 * Read persisted collapsed state from localStorage
 */
function getPersistedCollapsed(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return stored === 'true';
    } catch {
        return false;
    }
}

/**
 * Persist collapsed state to localStorage
 */
function persistCollapsed(collapsed: boolean): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
        // Ignore storage errors
    }
}

/**
 * Detect current breakpoint using matchMedia
 */
function detectBreakpoint(): { isMobile: boolean; isTablet: boolean } {
    if (typeof window === 'undefined') {
        return { isMobile: false, isTablet: false };
    }
    const width = window.innerWidth;
    const isMobile = width < BREAKPOINTS.lg;
    const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
    return { isMobile, isTablet };
}

/**
 * Create initial state for the layout reducer
 */
function createInitialState(): LayoutState {
    const { isMobile, isTablet } = detectBreakpoint();
    return {
        sidebarOpen: false,
        sidebarCollapsed: getPersistedCollapsed(),
        isMobile,
        isTablet,
        isDesktop: !isMobile,
    };
}

/**
 * Layout state reducer
 */
function layoutReducer(state: LayoutState, action: LayoutAction): LayoutState {
    switch (action.type) {
        case 'TOGGLE_SIDEBAR': {
            if (state.isMobile) {
                return { ...state, sidebarOpen: !state.sidebarOpen };
            }
            const newCollapsed = !state.sidebarCollapsed;
            persistCollapsed(newCollapsed);
            return { ...state, sidebarCollapsed: newCollapsed };
        }

        case 'OPEN_SIDEBAR':
            return { ...state, sidebarOpen: true };

        case 'CLOSE_SIDEBAR':
            return { ...state, sidebarOpen: false };

        case 'COLLAPSE_SIDEBAR':
            persistCollapsed(true);
            return { ...state, sidebarCollapsed: true };

        case 'EXPAND_SIDEBAR':
            persistCollapsed(false);
            return { ...state, sidebarCollapsed: false };

        case 'TOGGLE_COLLAPSE': {
            const newCollapsed = !state.sidebarCollapsed;
            persistCollapsed(newCollapsed);
            return { ...state, sidebarCollapsed: newCollapsed };
        }

        case 'SET_BREAKPOINT': {
            const { isMobile, isTablet } = action.payload;
            return {
                ...state,
                isMobile,
                isTablet,
                isDesktop: !isMobile,
                // Auto-close sidebar when switching to mobile
                sidebarOpen: isMobile ? false : state.sidebarOpen,
            };
        }

        default:
            return state;
    }
}

interface LayoutProviderProps {
    children: ReactNode;
}

/**
 * Provider component for layout state and actions
 *
 * Handles:
 * - Sidebar open/closed state (mobile drawer)
 * - Sidebar collapsed/expanded state (desktop narrow mode)
 * - Breakpoint detection via matchMedia
 * - Persisting collapsed preference to localStorage
 */
export function LayoutProvider({ children }: LayoutProviderProps) {
    const [state, dispatch] = useReducer(layoutReducer, undefined, createInitialState);

    // Set up breakpoint listeners
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQueryLg = window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`);
        const mediaQueryMd = window.matchMedia(`(min-width: ${BREAKPOINTS.md}px)`);

        function handleBreakpointChange() {
            const { isMobile, isTablet } = detectBreakpoint();
            dispatch({ type: 'SET_BREAKPOINT', payload: { isMobile, isTablet } });
        }

        // Listen for breakpoint changes
        mediaQueryLg.addEventListener('change', handleBreakpointChange);
        mediaQueryMd.addEventListener('change', handleBreakpointChange);

        return () => {
            mediaQueryLg.removeEventListener('change', handleBreakpointChange);
            mediaQueryMd.removeEventListener('change', handleBreakpointChange);
        };
    }, []);

    // Memoize actions to prevent unnecessary re-renders
    const toggleSidebar = useCallback(() => dispatch({ type: 'TOGGLE_SIDEBAR' }), []);
    const openSidebar = useCallback(() => dispatch({ type: 'OPEN_SIDEBAR' }), []);
    const closeSidebar = useCallback(() => dispatch({ type: 'CLOSE_SIDEBAR' }), []);
    const collapseSidebar = useCallback(() => dispatch({ type: 'COLLAPSE_SIDEBAR' }), []);
    const expandSidebar = useCallback(() => dispatch({ type: 'EXPAND_SIDEBAR' }), []);
    const toggleCollapse = useCallback(() => dispatch({ type: 'TOGGLE_COLLAPSE' }), []);

    const actions = useMemo<LayoutActions>(
        () => ({
            toggleSidebar,
            openSidebar,
            closeSidebar,
            collapseSidebar,
            expandSidebar,
            toggleCollapse,
        }),
        [toggleSidebar, openSidebar, closeSidebar, collapseSidebar, expandSidebar, toggleCollapse]
    );

    return (
        <LayoutStateContext value={state}>
            <LayoutActionsContext value={actions}>{children}</LayoutActionsContext>
        </LayoutStateContext>
    );
}
